/**
 * DemandView - Sprint 10 Phase 2 - Journey UX Wizard
 * Detail view with guided journey experience, section navigation,
 * history, transition actions, attachments, conversions, and AI assist
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Edit, Trash2, ArrowRight, Clock, User,
  Calendar, Target, TrendingUp, AlertTriangle, DollarSign,
  CheckCircle, XCircle, RefreshCw, MessageSquare, Paperclip,
  Upload, Download, File, X, ExternalLink, Briefcase, Cloud, Database,
  Sparkles, FileText, Shield, ClipboardCheck, Lightbulb, Save,
  FileImage, FileSpreadsheet, FileType
} from 'lucide-react';
import { toast } from 'sonner';
import {
  JourneyProgressStepper,
  NextStepGuidance,
  SectionNavigation,
  TimeInStageIndicator
} from '../components/DemandJourneyWizard';
import GuideMePanel from '../components/GuideMePanel';
import WhatIfAnalysisPanel from '../components/WhatIfAnalysisPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  intake: { color: 'bg-blue-100 text-blue-800', next: ['assessment', 'rejected'] },
  external_intake: { color: 'bg-cyan-100 text-cyan-800', next: ['intake', 'rejected'] },
  assessment: { color: 'bg-yellow-100 text-yellow-800', next: ['governance', 'intake', 'rejected'] },
  governance: { color: 'bg-purple-100 text-purple-800', next: ['approved', 'rejected', 'assessment'] },
  approved: { color: 'bg-green-100 text-green-800', next: [] },
  rejected: { color: 'bg-red-100 text-red-800', next: ['intake'] }
};

const IMPACT_COLORS = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-red-100 text-red-700'
};

// File type icons with tooltips helper
const getFileTypeLabel = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'Image File';
  if (['xlsx', 'xls', 'csv'].includes(ext)) return 'Spreadsheet';
  if (ext === 'pdf') return 'PDF Document';
  if (['pptx', 'ppt'].includes(ext)) return 'Presentation';
  if (['docx', 'doc'].includes(ext)) return 'Word Document';
  if (ext === 'txt') return 'Text File';
  return 'File';
};

const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  const label = getFileTypeLabel(filename);
  let icon;
  
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
    icon = <FileImage className="h-4 w-4 text-purple-500" />;
  } else if (['xlsx', 'xls', 'csv'].includes(ext)) {
    icon = <FileSpreadsheet className="h-4 w-4 text-green-500" />;
  } else if (ext === 'pdf') {
    icon = <FileType className="h-4 w-4 text-red-500" />;
  } else if (['pptx', 'ppt'].includes(ext)) {
    icon = <FileText className="h-4 w-4 text-orange-500" />;
  } else if (['docx', 'doc'].includes(ext)) {
    icon = <FileText className="h-4 w-4 text-blue-500" />;
  } else {
    icon = <File className="h-4 w-4 text-gray-400" />;
  }
  
  return (
    <div className="relative group flex-shrink-0" title={label}>
      {icon}
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
        {label}
      </span>
    </div>
  );
};

const DemandView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);

  const [demand, setDemand] = useState(null);
  const [history, setHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionComment, setTransitionComment] = useState('');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [pendingTransition, setPendingTransition] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [converting, setConverting] = useState(null);
  // AI Assist state
  const [aiLoading, setAiLoading] = useState(null);
  const [aiResults, setAiResults] = useState({});
  // Journey UX state - Sprint 10
  const [activeSection, setActiveSection] = useState('overview');
  const [showJourneyView, setShowJourneyView] = useState(true);
  // Guide-Me Panel state - Sprint 16
  const [showGuideMePanel, setShowGuideMePanel] = useState(false);
  // Sprint 22: Multi-Project Conversion state
  const [showMultiConvert, setShowMultiConvert] = useState(false);
  const [multiConvertItems, setMultiConvertItems] = useState([
    { type: 'epic', title: '', target_project: '', description: '' }
  ]);
  const [multiConverting, setMultiConverting] = useState(false);
  const [conversionResults, setConversionResults] = useState(null);
  const [portfolioPosition, setPortfolioPosition] = useState(null);
  // Sprint 23: What-If Analysis state
  const [showWhatIfPanel, setShowWhatIfPanel] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    // TENANT ISOLATION: Include X-Tenant-ID header for sysadmin impersonation
    const impersonatedTenantId = localStorage.getItem('impersonated_tenant_id');
    const userTenantId = localStorage.getItem('simpliview_user_tenant_id');
    const activeTenantId = impersonatedTenantId || userTenantId;
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    
    if (activeTenantId) {
      headers['X-Tenant-ID'] = activeTenantId;
    }
    
    return headers;
  }, []);

  const fetchDemand = useCallback(async () => {
    try {
      const [demandRes, historyRes, attachmentsRes, linksRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/demands/${id}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/v2/demands/${id}/history`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/v2/demands/${id}/attachments`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/v2/demands/${id}/links`, { headers: getAuthHeaders() })
      ]);

      if (demandRes.ok) {
        const data = await demandRes.json();
        setDemand(data.data);
      }

      if (historyRes.ok) {
        const data = await historyRes.json();
        setHistory(data.data || []);
      }

      if (attachmentsRes.ok) {
        const data = await attachmentsRes.json();
        setAttachments(data.data || []);
      }

      if (linksRes.ok) {
        const data = await linksRes.json();
        setLinks(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch demand:', err);
      toast.error('Failed to load demand');
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders]);

  useEffect(() => {
    fetchDemand();
  }, [fetchDemand]);

  const handleTransition = async (toStatus) => {
    // For governance decisions, prompt for comment
    if (toStatus === 'approved' || toStatus === 'rejected') {
      setPendingTransition(toStatus);
      setShowCommentModal(true);
      return;
    }

    executeTransition(toStatus, '');
  };

  const executeTransition = async (toStatus, comment) => {
    setTransitioning(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/demands/${id}/transition`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ to: toStatus, comment })
      });

      if (response.ok) {
        toast.success(`Demand moved to ${toStatus}`);
        setShowCommentModal(false);
        setTransitionComment('');
        fetchDemand();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Transition failed');
      }
    } catch (err) {
      console.error('Transition failed:', err);
      toast.error('Failed to transition demand');
    } finally {
      setTransitioning(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this demand?')) return;

    try {
      const response = await fetch(`${API_URL}/api/v2/demands/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Demand deleted');
        navigate('/demands');
      } else {
        toast.error('Failed to delete demand');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete demand');
    }
  };

  // Attachment handlers
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['pdf', 'docx', 'xlsx', 'png', 'jpg', 'jpeg', 'txt'];
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast.error(`File type not allowed. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('simpliview_token');
      const response = await fetch(`${API_URL}/api/v2/demands/${id}/attachments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        toast.success('File uploaded successfully');
        fetchDemand();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (fileId) => {
    if (!window.confirm('Delete this attachment?')) return;

    try {
      const response = await fetch(`${API_URL}/api/v2/demands/${id}/attachments/${fileId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Attachment deleted');
        fetchDemand();
      } else {
        toast.error('Failed to delete attachment');
      }
    } catch (err) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = (fileId, filename) => {
    const token = localStorage.getItem('simpliview_token');
    window.open(`${API_URL}/api/v2/demands/${id}/attachments/${fileId}?token=${token}`, '_blank');
  };

  // Conversion handlers
  const handleConvert = async (target) => {
    if (!window.confirm(`Convert this demand to a ${target === 'project' ? 'ScopeOps Project' : target === 'ado' ? 'Azure DevOps Epic' : 'Jira Epic'}?`)) return;

    setConverting(target);
    try {
      const response = await fetch(`${API_URL}/api/v2/demands/${id}/convert/${target}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(result.message || 'Conversion successful!');
        fetchDemand();
      } else {
        toast.error(result.detail || result.message || 'Conversion failed');
      }
    } catch (err) {
      console.error('Conversion failed:', err);
      toast.error('Failed to convert demand');
    } finally {
      setConverting(null);
    }
  };

  // Sprint 22: Multi-Project Conversion handler
  const handleMultiConvert = async () => {
    // Validate items
    const validItems = multiConvertItems.filter(item => item.title.trim());
    if (validItems.length === 0) {
      toast.error('Please add at least one item with a title');
      return;
    }
    
    setMultiConverting(true);
    try {
      const response = await fetch(`${API_URL}/api/demands/${id}/convert-multiple`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: validItems.map(item => ({
            type: item.type,
            title: item.title,
            description: item.description || `Created from demand: ${attrs?.title}`,
            target_project: item.target_project || null
          }))
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        setConversionResults(result);
        toast.success(`Created ${result.created_items?.length || 0} items successfully`);
        fetchDemand();
      } else {
        toast.error(result.detail || 'Multi-conversion failed');
      }
    } catch (err) {
      console.error('Multi-conversion failed:', err);
      toast.error('Failed to create multiple items');
    } finally {
      setMultiConverting(false);
    }
  };

  const addMultiConvertItem = () => {
    setMultiConvertItems(prev => [...prev, { type: 'feature', title: '', target_project: '', description: '' }]);
  };

  const removeMultiConvertItem = (index) => {
    setMultiConvertItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateMultiConvertItem = (index, field, value) => {
    setMultiConvertItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  // Sprint 22: Fetch portfolio position
  const fetchPortfolioPosition = async () => {
    try {
      const response = await fetch(`${API_URL}/api/demands/${id}/portfolio-position`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPortfolioPosition(data);
      }
    } catch (err) {
      console.error('Failed to fetch portfolio position:', err);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // AI Assist handlers
  const handleAiAction = async (action) => {
    setAiLoading(action);
    try {
      const response = await fetch(`${API_URL}/api/v2/demands/${id}/ai/${action}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        setAiResults(prev => ({ ...prev, [action]: result.data }));
        toast.success('AI analysis complete');
      } else if (response.status === 503) {
        toast.error('AI service not available');
      } else {
        toast.error('AI analysis failed');
      }
    } catch (err) {
      console.error('AI action failed:', err);
      toast.error('Failed to run AI analysis');
    } finally {
      setAiLoading(null);
    }
  };

  const clearAiResult = (action) => {
    setAiResults(prev => {
      const newResults = { ...prev };
      delete newResults[action];
      return newResults;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!demand) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Demand not found</p>
        <Button onClick={() => navigate('/demands')} className="mt-4">
          Back to Demands
        </Button>
      </div>
    );
  }

  const attrs = demand.attributes || {};
  const statusConfig = STATUS_CONFIG[attrs.status] || {};

  // Handle section-based AI actions
  const handleSectionAIAction = (section) => {
    const sectionToAction = {
      'overview': 'governance_summary',
      'problem': 'improve_description',
      'benefits': 'governance_summary',
      'requirements': 'acceptance_criteria',
      'risks': 'risks',
      'scoring': 'scoring_suggestions',
      'analyze': 'governance_summary',
      'suggest_next': 'acceptance_criteria'
    };
    const action = sectionToAction[section] || 'governance_summary';
    handleAIAction(action);
  };

  // Navigate to section
  const navigateToSection = (section) => {
    setActiveSection(section);
    // Scroll to section element if it exists
    const element = document.getElementById(`section-${section}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="demand-view">
      {/* Header with Journey View Toggle */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/demands')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold">{attrs.title}</h1>
              <Badge className={statusConfig.color}>{attrs.status}</Badge>
              {attrs.source === 'public' && (
                <Badge className="bg-cyan-100 text-cyan-700">External</Badge>
              )}
              {attrs.opex_flag && (
                <Badge className="bg-orange-100 text-orange-700">OPEX</Badge>
              )}
            </div>
            <div className="flex items-center gap-3 text-gray-500 text-sm">
              <span>Created {attrs.created_at ? new Date(attrs.created_at).toLocaleDateString() : '-'}</span>
              <span>•</span>
              <span>by {attrs.created_by || attrs.requestor || 'Unknown'}</span>
              <TimeInStageIndicator demand={demand} history={history} />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowJourneyView(!showJourneyView)}
            className="text-xs"
          >
            {showJourneyView ? 'Classic View' : 'Journey View'}
          </Button>
          <Button variant="outline" onClick={() => navigate(`/demands/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="outline" className="text-red-500" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Journey Progress Stepper */}
      {showJourneyView && (
        <>
          <JourneyProgressStepper currentStatus={attrs.status} demand={demand} />
          <NextStepGuidance 
            currentStatus={attrs.status} 
            demand={demand} 
            onNavigateToSection={navigateToSection}
          />
        </>
      )}

      {/* Transition Actions */}
      {statusConfig.next?.length > 0 && (
        <Card className="mb-6 bg-gradient-to-r from-gray-50 to-white border-l-4 border-l-indigo-500">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-600">Move to next stage:</span>
                {statusConfig.next.map(nextStatus => (
                  <Button
                    key={nextStatus}
                    size="sm"
                    variant={nextStatus === 'approved' ? 'default' : nextStatus === 'rejected' ? 'destructive' : 'outline'}
                    onClick={() => handleTransition(nextStatus)}
                    disabled={transitioning}
                    data-testid={`transition-${nextStatus}`}
                  >
                    <ArrowRight className="h-4 w-4 mr-1" />
                    {nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Layout with Section Navigation */}
      <div className={`grid gap-6 ${showJourneyView ? 'grid-cols-4' : 'grid-cols-3'}`}>
        {/* Section Navigation - Left Sidebar (Journey View Only) */}
        {showJourneyView && (
          <div className="col-span-1">
            <div className="sticky top-6">
              <SectionNavigation
                activeSection={activeSection}
                onSectionChange={navigateToSection}
                demand={demand}
                showAIActions={true}
                onAIAction={handleSectionAIAction}
              />
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className={showJourneyView ? 'col-span-2' : 'col-span-2'} data-testid="main-content">
          <div className="space-y-6">
            {/* Overview Section */}
            <Card id="section-overview">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Overview
                  </CardTitle>
                  <CardDescription>Basic information about this demand</CardDescription>
                </div>
                {showJourneyView && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleSectionAIAction('overview')}
                    disabled={aiLoading}
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    AI Assist
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Department:</span>
                    <span className="ml-2 font-medium">{attrs.department || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Category:</span>
                    <span className="ml-2 font-medium">{attrs.category || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Requestor:</span>
                    <span className="ml-2 font-medium">{attrs.requestor || attrs.created_by || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Source:</span>
                    <Badge variant="outline" className="ml-2">
                      {attrs.source === 'public' ? 'External Intake' : 'Internal'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Problem Statement Section */}
            <Card id="section-problem">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Problem Statement
                  </CardTitle>
                </div>
                {showJourneyView && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleSectionAIAction('problem')}
                    disabled={aiLoading}
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Improve
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {attrs.description || 'No description provided'}
                </p>
              </CardContent>
            </Card>

            {/* Benefits Section */}
            <Card id="section-benefits">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Expected Benefits
                  </CardTitle>
                </div>
                {showJourneyView && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleSectionAIAction('benefits')}
                    disabled={aiLoading}
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Suggest
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {attrs.business_outcomes ? (
                  <p className="text-gray-700 whitespace-pre-wrap">{attrs.business_outcomes}</p>
                ) : (
                  <p className="text-gray-400 italic">No benefits documented yet</p>
                )}
              </CardContent>
            </Card>

            {/* Requirements Section */}
            <Card id="section-requirements">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4" />
                    Requirements
                  </CardTitle>
                </div>
                {showJourneyView && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleSectionAIAction('requirements')}
                    disabled={aiLoading}
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Generate Criteria
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {attrs.requirements ? (
                  <p className="text-gray-700 whitespace-pre-wrap">{attrs.requirements}</p>
                ) : (
                  <p className="text-gray-400 italic">No requirements documented yet</p>
                )}
              </CardContent>
            </Card>

            {/* Risks Section */}
            <Card id="section-risks">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Risks & Dependencies
                  </CardTitle>
                </div>
                {showJourneyView && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleSectionAIAction('risks')}
                    disabled={aiLoading}
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Identify Risks
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {attrs.risks ? (
                  <p className="text-gray-700 whitespace-pre-wrap">{attrs.risks}</p>
                ) : (
                  <p className="text-gray-400 italic">No risks documented yet</p>
                )}
              </CardContent>
            </Card>

            {/* Transition History */}
            <Card id="section-history">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Transition History
                </CardTitle>
                <CardDescription>Timeline of status changes</CardDescription>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <p className="text-gray-500 text-sm">No transitions yet</p>
                ) : (
                  <div className="space-y-4">
                    {history.map((h, idx) => (
                      <div key={h.id || idx} className="flex items-start gap-3 pb-4 border-b last:border-0">
                        <div className={`p-2 rounded-full ${
                          h.to_status === 'approved' ? 'bg-green-100' :
                          h.to_status === 'rejected' ? 'bg-red-100' :
                          'bg-blue-100'
                        }`}>
                          {h.to_status === 'approved' ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : h.to_status === 'rejected' ? (
                            <XCircle className="h-4 w-4 text-red-600" />
                          ) : (
                            <ArrowRight className="h-4 w-4 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">
                              {h.from_status} → {h.to_status}
                            </span>
                            <span className="text-xs text-gray-400">
                              ({h.days_in_previous_state || 0} days in {h.from_status})
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {h.transitioned_at ? new Date(h.transitioned_at).toLocaleString() : '-'}
                            {h.transitioned_by && ` by ${h.transitioned_by}`}
                          </div>
                          {h.comment && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-600 flex items-start gap-2">
                              <MessageSquare className="h-4 w-4 mt-0.5 text-gray-400" />
                              {h.comment}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card id="section-attachments">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Attachments
                </CardTitle>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.txt"
                  data-testid="attachment-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-attachment-btn"
                >
                  {uploading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-1" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
              <CardDescription>Max 10MB. Supported: PDF, DOCX, XLSX, PNG, JPG, TXT</CardDescription>
            </CardHeader>
            <CardContent>
              {attachments.length === 0 ? (
                <p className="text-gray-500 text-sm">No attachments yet</p>
              ) : (
                <div className="space-y-2">
                  {attachments.map((att) => (
                    <div 
                      key={att.file_id} 
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                      data-testid={`attachment-${att.file_id}`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {getFileIcon(att.filename)}
                        <span className="text-sm truncate">{att.filename}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          ({formatFileSize(att.file_size)})
                        </span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadAttachment(att.file_id, att.filename)}
                          data-testid={`download-${att.file_id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500"
                          onClick={() => handleDeleteAttachment(att.file_id)}
                          data-testid={`delete-${att.file_id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Conversion Links - Sprint 8 */}
          {links.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Linked Resources
                </CardTitle>
                <CardDescription>Created from this demand</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {links.map((link) => (
                    <a
                      key={link.id}
                      href={link.link_url}
                      target={link.external_system === 'project' ? '_self' : '_blank'}
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      data-testid={`link-${link.external_system}`}
                    >
                      {link.external_system === 'project' && <Briefcase className="h-4 w-4 text-blue-500" />}
                      {link.external_system === 'ado' && <Cloud className="h-4 w-4 text-blue-600" />}
                      {link.external_system === 'jira' && <Database className="h-4 w-4 text-blue-500" />}
                      <span className="text-sm font-medium">
                        {link.external_system === 'project' ? 'ScopeOps Project' : 
                         link.external_system === 'ado' ? 'Azure DevOps Epic' : 'Jira Epic'}
                      </span>
                      <span className="text-xs text-gray-500">{link.external_key}</span>
                      <ExternalLink className="h-3 w-3 text-gray-400 ml-auto" />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          </div>
        </div>

        {/* Sidebar */}
        <div className={showJourneyView ? 'col-span-1' : 'col-span-1'}>
          <div className="space-y-6 sticky top-6">
          {/* Score */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Priority Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-4xl font-bold mb-2 ${
                attrs.score >= 7 ? 'text-green-600' :
                attrs.score >= 4 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {attrs.score?.toFixed(1) || '0.0'}
              </div>
              <p className="text-sm text-gray-500">
                Based on impact, urgency, strategic alignment, and complexity
              </p>
            </CardContent>
          </Card>

          {/* Details */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Requestor:</span>
                <span className="font-medium">{attrs.requestor || '-'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Target className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Department:</span>
                <span className="font-medium">{attrs.department || '-'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Category:</span>
                <span className="font-medium">{attrs.category || '-'}</span>
              </div>
              {attrs.cost_estimate && (
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Est. Cost:</span>
                  <span className="font-medium">${attrs.cost_estimate.toLocaleString()}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assessment */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Assessment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Impact</span>
                <Badge className={IMPACT_COLORS[attrs.impact]}>{attrs.impact}</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Urgency</span>
                <Badge className={IMPACT_COLORS[attrs.urgency]}>{attrs.urgency}</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Strategic Alignment</span>
                <span className="font-medium">{attrs.strategic_alignment}/5</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Complexity</span>
                <span className="font-medium">{attrs.complexity}/5</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Business Value</span>
                <span className="font-medium">{attrs.business_value}/5</span>
              </div>
            </CardContent>
          </Card>

          {/* Conversion Actions - Sprint 8 - Only for approved demands */}
          {attrs.status === 'approved' && (
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                  <TrendingUp className="h-4 w-4" />
                  Convert Demand
                </CardTitle>
                <CardDescription>Create resources from this approved demand</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {!links.find(l => l.external_system === 'project') && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleConvert('project')}
                    disabled={converting !== null}
                    data-testid="convert-to-project"
                  >
                    {converting === 'project' ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Briefcase className="h-4 w-4 mr-2 text-blue-500" />
                    )}
                    Create ScopeOps Project
                  </Button>
                )}
                {!links.find(l => l.external_system === 'ado') && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleConvert('ado')}
                    disabled={converting !== null}
                    data-testid="convert-to-ado"
                  >
                    {converting === 'ado' ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Cloud className="h-4 w-4 mr-2 text-blue-600" />
                    )}
                    Create Azure DevOps Epic
                  </Button>
                )}
                {!links.find(l => l.external_system === 'jira') && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleConvert('jira')}
                    disabled={converting !== null}
                    data-testid="convert-to-jira"
                  >
                    {converting === 'jira' ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Database className="h-4 w-4 mr-2 text-blue-500" />
                    )}
                    Create Jira Epic
                  </Button>
                )}
                {links.length === 3 && (
                  <p className="text-sm text-green-700 text-center py-2">
                    All conversions completed!
                  </p>
                )}
                
                {/* Sprint 22: Multi-Project Conversion */}
                <div className="pt-3 mt-3 border-t border-green-200">
                  <Button
                    variant="outline"
                    className="w-full justify-start bg-white hover:bg-green-50"
                    onClick={() => setShowMultiConvert(!showMultiConvert)}
                    data-testid="multi-convert-toggle"
                  >
                    <Target className="h-4 w-4 mr-2 text-green-600" />
                    Create Multiple Items...
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sprint 22: Multi-Project Conversion Panel */}
          {attrs.status === 'approved' && showMultiConvert && (
            <Card className="bg-white border-2 border-green-300">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5 text-green-600" />
                  Multi-Item Conversion
                </CardTitle>
                <CardDescription>
                  Create multiple Epics, Features, Stories, or Tasks from this demand
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Conversion Items */}
                {multiConvertItems.map((item, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <select
                        className="px-2 py-1 border rounded text-sm"
                        value={item.type}
                        onChange={(e) => updateMultiConvertItem(index, 'type', e.target.value)}
                        data-testid={`item-type-${index}`}
                      >
                        <option value="epic">Epic</option>
                        <option value="feature">Feature</option>
                        <option value="story">Story</option>
                        <option value="task">Task</option>
                      </select>
                      <input
                        type="text"
                        className="flex-1 px-2 py-1 border rounded text-sm"
                        placeholder="Item title..."
                        value={item.title}
                        onChange={(e) => updateMultiConvertItem(index, 'title', e.target.value)}
                        data-testid={`item-title-${index}`}
                      />
                      {multiConvertItems.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMultiConvertItem(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <input
                      type="text"
                      className="w-full px-2 py-1 border rounded text-sm"
                      placeholder="Description (optional)..."
                      value={item.description}
                      onChange={(e) => updateMultiConvertItem(index, 'description', e.target.value)}
                    />
                  </div>
                ))}
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addMultiConvertItem}
                    data-testid="add-convert-item"
                  >
                    + Add Item
                  </Button>
                  <div className="flex-1" />
                  <Button
                    variant="default"
                    onClick={handleMultiConvert}
                    disabled={multiConverting || multiConvertItems.every(i => !i.title.trim())}
                    data-testid="execute-multi-convert"
                  >
                    {multiConverting ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Create {multiConvertItems.filter(i => i.title.trim()).length} Items
                  </Button>
                </div>
                
                {/* Conversion Results */}
                {conversionResults && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Conversion Complete
                    </h4>
                    <div className="space-y-1">
                      {conversionResults.created_items?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm p-2 bg-white rounded">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{item.type}</Badge>
                            <span className="font-medium">{item.title}</span>
                          </div>
                          <Badge className={item.status === 'created' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                            {item.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                    {conversionResults.traceability_id && (
                      <p className="text-xs text-green-600 mt-2">
                        Traceability ID: {conversionResults.traceability_id}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Sprint 22: Portfolio Position Card */}
          {portfolioPosition && (
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                  <Briefcase className="h-4 w-4" />
                  Portfolio Position
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm space-y-2">
                  {portfolioPosition.portfolio && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Portfolio:</span>
                      <Badge variant="outline">{portfolioPosition.portfolio.name}</Badge>
                    </div>
                  )}
                  {portfolioPosition.program && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Program:</span>
                      <Badge variant="outline">{portfolioPosition.program.name}</Badge>
                    </div>
                  )}
                  {portfolioPosition.project && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Project:</span>
                      <Badge variant="outline">{portfolioPosition.project.name}</Badge>
                    </div>
                  )}
                  {portfolioPosition.alignment_badges?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {portfolioPosition.alignment_badges.map((badge, idx) => (
                        <Badge 
                          key={idx} 
                          className={
                            badge.type === 'strategic' ? 'bg-purple-100 text-purple-800' :
                            badge.type === 'capacity' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }
                        >
                          {badge.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto text-blue-600"
                    onClick={() => navigate('/portfolio/roadmap')}
                    data-testid="view-portfolio-position"
                  >
                    View Portfolio Position →
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sprint 23: What-If Analysis Card */}
          {attrs.status === 'approved' && (
            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-purple-800">
                  <Sparkles className="h-4 w-4" />
                  What-If Analysis
                </CardTitle>
                <CardDescription>Simulate project structures before committing</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-purple-700 mb-3">
                  Explore different ways to structure this demand as projects and compare capacity, budget, and timeline impacts.
                </p>
                <Button
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => setShowWhatIfPanel(true)}
                  data-testid="open-whatif-analysis"
                >
                  <Target className="h-4 w-4 mr-2" />
                  Open What-If Analysis
                </Button>
              </CardContent>
            </Card>
          )}

          {/* AI Assist - Sprint 8 */}
          <Card className="bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-purple-800">
                <Sparkles className="h-4 w-4" />
                AI Assist
              </CardTitle>
              <CardDescription>AI-powered analysis and content generation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start text-left"
                onClick={() => handleAiAction('acceptance-criteria')}
                disabled={aiLoading !== null}
                data-testid="ai-acceptance-criteria"
              >
                {aiLoading === 'acceptance-criteria' ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ClipboardCheck className="h-4 w-4 mr-2 text-green-500" />
                )}
                Generate Acceptance Criteria
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start text-left"
                onClick={() => handleAiAction('risks')}
                disabled={aiLoading !== null}
                data-testid="ai-risks"
              >
                {aiLoading === 'risks' ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Shield className="h-4 w-4 mr-2 text-orange-500" />
                )}
                Generate Risk Assessment
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start text-left"
                onClick={() => handleAiAction('governance-summary')}
                disabled={aiLoading !== null}
                data-testid="ai-governance-summary"
              >
                {aiLoading === 'governance-summary' ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4 mr-2 text-blue-500" />
                )}
                Generate Governance Summary
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start text-left"
                onClick={() => handleAiAction('suggest-scoring')}
                disabled={aiLoading !== null}
                data-testid="ai-suggest-scoring"
              >
                {aiLoading === 'suggest-scoring' ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Lightbulb className="h-4 w-4 mr-2 text-yellow-500" />
                )}
                Suggest Scoring
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start text-left"
                onClick={() => handleAiAction('improve-description')}
                disabled={aiLoading !== null}
                data-testid="ai-improve-description"
              >
                {aiLoading === 'improve-description' ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Edit className="h-4 w-4 mr-2 text-purple-500" />
                )}
                Improve Description
              </Button>
            </CardContent>
          </Card>

          {/* AI Results Display */}
          {Object.keys(aiResults).length > 0 && (
            <Card className="border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  AI Analysis Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {aiResults['acceptance-criteria'] && (
                  <div className="bg-white p-3 rounded-lg border" data-testid="ai-result-criteria">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-green-700 flex items-center gap-1">
                        <ClipboardCheck className="h-4 w-4" /> Acceptance Criteria
                      </h4>
                      <Button size="sm" variant="ghost" onClick={() => clearAiResult('acceptance-criteria')}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    <ul className="text-sm space-y-1">
                      {aiResults['acceptance-criteria'].criteria?.map((c, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="h-3 w-3 text-green-500 mt-1 flex-shrink-0" />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {aiResults['risks'] && (
                  <div className="bg-white p-3 rounded-lg border" data-testid="ai-result-risks">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-orange-700 flex items-center gap-1">
                        <Shield className="h-4 w-4" /> Risk Assessment
                      </h4>
                      <Button size="sm" variant="ghost" onClick={() => clearAiResult('risks')}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="text-sm space-y-2">
                      {aiResults['risks'].risks?.map((r, i) => (
                        <div key={i} className="border-l-2 border-orange-300 pl-2">
                          <p className="font-medium">{r.risk}</p>
                          <p className="text-xs text-gray-500">Impact: {r.impact} | Mitigation: {r.mitigation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {aiResults['governance-summary'] && (
                  <div className="bg-white p-3 rounded-lg border" data-testid="ai-result-summary">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-blue-700 flex items-center gap-1">
                        <FileText className="h-4 w-4" /> Governance Summary
                      </h4>
                      <Button size="sm" variant="ghost" onClick={() => clearAiResult('governance-summary')}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="text-sm space-y-2">
                      <p>{aiResults['governance-summary'].synopsis}</p>
                      <p className="text-gray-600"><strong>Value:</strong> {aiResults['governance-summary'].value_summary}</p>
                      <p className="text-gray-600"><strong>Risks:</strong> {aiResults['governance-summary'].risk_assessment}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={
                          aiResults['governance-summary'].recommendation === 'approve' ? 'bg-green-100 text-green-700' :
                          aiResults['governance-summary'].recommendation === 'reject' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }>
                          {aiResults['governance-summary'].recommendation}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          Confidence: {(aiResults['governance-summary'].confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {aiResults['suggest-scoring'] && (
                  <div className="bg-white p-3 rounded-lg border" data-testid="ai-result-scoring">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-yellow-700 flex items-center gap-1">
                        <Lightbulb className="h-4 w-4" /> Scoring Suggestions
                      </h4>
                      <Button size="sm" variant="ghost" onClick={() => clearAiResult('suggest-scoring')}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="text-sm grid grid-cols-2 gap-2">
                      <div>Impact: <Badge>{aiResults['suggest-scoring'].impact}</Badge></div>
                      <div>Urgency: <Badge>{aiResults['suggest-scoring'].urgency}</Badge></div>
                      <div>Alignment: <Badge>{aiResults['suggest-scoring'].strategic_alignment}/5</Badge></div>
                      <div>Value: <Badge>{aiResults['suggest-scoring'].business_value}/5</Badge></div>
                      <div>Complexity: <Badge>{aiResults['suggest-scoring'].complexity}/5</Badge></div>
                    </div>
                    {aiResults['suggest-scoring'].reasoning && (
                      <p className="text-xs text-gray-500 mt-2">{aiResults['suggest-scoring'].reasoning}</p>
                    )}
                  </div>
                )}
                
                {aiResults['improve-description'] && (
                  <div className="bg-white p-3 rounded-lg border" data-testid="ai-result-description">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-purple-700 flex items-center gap-1">
                        <Edit className="h-4 w-4" /> Improved Description
                      </h4>
                      <Button size="sm" variant="ghost" onClick={() => clearAiResult('improve-description')}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="text-sm space-y-2">
                      <p className="whitespace-pre-wrap">{aiResults['improve-description'].improved_description}</p>
                      {aiResults['improve-description'].suggested_outcomes?.length > 0 && (
                        <div>
                          <p className="font-medium text-gray-600">Suggested Outcomes:</p>
                          <ul className="list-disc list-inside text-gray-500">
                            {aiResults['improve-description'].suggested_outcomes.map((o, i) => (
                              <li key={i}>{o}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          </div>
        </div>
      </div>

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {pendingTransition === 'approved' ? 'Approve Demand' : 'Reject Demand'}
              </CardTitle>
              <CardDescription>
                Add a comment explaining the decision
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <textarea
                value={transitionComment}
                onChange={(e) => setTransitionComment(e.target.value)}
                className="w-full px-3 py-2 border rounded-md h-24"
                placeholder="Enter your comment..."
                data-testid="transition-comment"
              />
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCommentModal(false);
                    setTransitionComment('');
                    setPendingTransition(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant={pendingTransition === 'approved' ? 'default' : 'destructive'}
                  onClick={() => executeTransition(pendingTransition, transitionComment)}
                  disabled={transitioning}
                >
                  {transitioning ? 'Processing...' : (pendingTransition === 'approved' ? 'Approve' : 'Reject')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Guide-Me Panel - Sprint 16 */}
      <GuideMePanel
        demandId={id}
        demandData={demand}
        isOpen={showGuideMePanel}
        onToggle={() => setShowGuideMePanel(!showGuideMePanel)}
        onUpdateDemand={(updates) => {
          // Handle updates from AI suggestions
          setDemand(prev => ({ ...prev, ...updates }));
        }}
      />
      
      {/* Sprint 23: What-If Analysis Dialog */}
      {showWhatIfPanel && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto p-6">
            <WhatIfAnalysisPanel
              demandId={id}
              demandTitle={attrs?.title || 'Demand'}
              demandBudget={attrs?.estimated_cost || attrs?.budget || 100000}
              onClose={() => setShowWhatIfPanel(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DemandView;
